import type { WorkspacePresence } from "@/features/presence/types";

import { useQuery } from "@tanstack/react-query";

import { ME_CACHE_KEY } from "./keys";

import {
  applyDevAccess,
  getDevRoleOverride,
} from "@/components/dev-tweaks/helpers";
import { fetchPermissionCatalog } from "@/hooks/api/role/usePermissionRules";
import { isMockEnabled, simulateDelay } from "@/mock/config";
import { getMeAccessMock } from "@/mock/handlers/role";
import { getMeMock } from "@/mock/handlers/user";
import { useGlobalStore } from "@/stores/global";
import { Response } from "@/types/pagination";
import { User } from "@/types/user";
import { api } from "@/lib/api-client";

export type MeResponse = Omit<User, "presence"> & {
  presence: Pick<WorkspacePresence, "status" | "lastSeenAt"> | null;
};

const fetchMe = async () => {
  if (isMockEnabled()) {
    await simulateDelay();

    return { ...getMeMock(), access: getMeAccessMock() };
  }

  const { data } = await api.get("users/me").json<Response<MeResponse>>();

  return data;
};

// `access` is for the workspace in the request header, so the key carries it.
// Bootstrap waits on it, so it starts with the workspace, not after the user list.
export const useGetMe = () => {
  const activeWorkspaceId = useGlobalStore((state) => state.activeWorkspaceId);

  return useQuery({
    queryKey: [...ME_CACHE_KEY, activeWorkspaceId],
    enabled: Boolean(activeWorkspaceId),
    staleTime: 10_000,
    queryFn: async () => {
      const me = await fetchMe();
      const devRole = getDevRoleOverride();

      // Dev-only role simulation: `useRoles` reads `access`, so it is applied here.
      if (!devRole || !me.access) return me;

      const { presets } = await fetchPermissionCatalog(activeWorkspaceId);

      return { ...me, access: applyDevAccess(me.access, devRole, presets) };
    },
  });
};
